import { motion } from 'framer-motion';

interface LoadingStateProps {
  message?: string;
  variant?: 'spinner' | 'skeleton' | 'pulse' | 'dots';
  size?: 'sm' | 'md' | 'lg';
}

/**
 * LoadingState Component
 * 
 * Beautiful loading animations that maintain user engagement
 * while content is being fetched or processed.
 * 
 * Variants:
 * - spinner: Classic rotating spinner
 * - skeleton: Content skeleton (for lists/cards)
 * - pulse: Pulsing glow effect
 * - dots: Three animated dots
 * 
 * @example
 * <LoadingState message="Loading your tabs..." variant="spinner" />
 */
export function LoadingState({ 
  message = 'Loading...', 
  variant = 'spinner',
  size = 'md'
}: LoadingStateProps) {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
  };

    if (variant === 'spinner') {
      return (
        <div className="flex flex-col items-center justify-center gap-4 p-8">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className={`${sizeClasses[size]} rounded-full border-2 border-primary-500/20 border-t-primary-500`}
          />
          {message && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-khiimori-text-muted text-sm"
            >
              {message}
            </motion.p>
          )}
        </div>
      );
    }

    if (variant === 'dots') {
      return (
        <div className="flex flex-col items-center justify-center gap-4 p-8">
          <div className="flex items-center gap-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5] 
                }}
                transition={{ 
                  duration: 1, 
                  repeat: Infinity, 
                  delay: i * 0.2 
                }}
                className="w-3 h-3 rounded-full bg-primary-500"
              />
            ))}
          </div>
          {message && (
            <p className="text-khiimori-text-muted text-sm">{message}</p>
          )}
        </div>
      );
    }

  if (variant === 'pulse') {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8">
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{ 
            duration: 2, 
            repeat: Infinity,
            ease: 'easeInOut'
          }}
          className={`${sizeClasses[size]} rounded-full bg-gradient-primary`}
        />
        {message && (
          <p className="text-khiimori-text-muted text-sm">{message}</p>
        )}
      </div>
    );
  }

  // skeleton variant
  return (
    <div className="space-y-4 p-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-khiimori-bg-tertiary/50 shimmer" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-khiimori-bg-tertiary/50 rounded shimmer" style={{ width: '60%' }} />
            <div className="h-3 bg-khiimori-bg-tertiary/50 rounded shimmer" style={{ width: '40%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * EmptyState Component
 * 
 * A friendly empty state that guides users on what to do next.
 * Reduces cognitive load by providing clear next steps.
 * 
 * @example
 * <EmptyState
 *   icon={Inbox}
 *   title="No tabs yet"
 *   description="Open some tabs to get started with intelligent organization"
 *   action={<button>Open Browser Extension</button>}
 * />
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: any;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-center justify-center p-12 text-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', delay: 0.1 }}
        className="w-16 h-16 rounded-2xl mb-4 flex items-center justify-center bg-primary-500/10 border border-primary-500/20"
      >
        <Icon className="w-8 h-8 text-primary-500" />
      </motion.div>
      
      <h3 className="text-xl font-semibold text-khiimori-text-primary mb-2">{title}</h3>
      <p className="text-khiimori-text-muted max-w-md mb-6">{description}</p>
      
      {action && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {action}
        </motion.div>
      )}
    </motion.div>
  );
}

/**
 * PageLoader Component
 * 
 * Full-page loading overlay with smooth transitions.
 * Used for page-level loading states.
 */
export function PageLoader({ message = 'Loading...' }: { message?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-khiimori-bg-secondary/80 backdrop-blur-sm"
    >
      <div className="text-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-primary-500/20 border-t-primary-500"
        />
        <p className="text-khiimori-text-primary text-lg font-medium">{message}</p>
      </div>
    </motion.div>
  );
}
